/**
 * Codex Design System — Dark Mode Semantic Colors
 *
 * Token values are calibrated for WCAG 2.x AAA on text/background pairs.
 * Border colors and saturated role/fill colors are kept at their original
 * brand-identity values — borders are visual chrome (1.4.11 doesn't apply
 * uniformly) and role colors give buttons their visual weight against the
 * dark page background.
 *
 * Audit notes (text tokens vs. previous values):
 *   textBrand     #7841F4 → #D7C8F8  (was 2.6:1 fail on bgElMd, now 9.2:1 AAA)
 *   textLink      #55B2E0 → #A6D5EC  (was 6.0:1 AA, now 9.1:1 AAA)
 *   textError     #FE3752 → #FB97A5  (better contrast on elevated surfaces)
 */

import { palettes } from '../tokens/palettes';
import type { SemanticColors } from './types';

export const darkColors: SemanticColors = {
  mode: 'dark',

  // Surfaces — progressively lighter with elevation (Figma Foundations §11.2)
  bgBody: '#0F0F0F', // level 0 — flat page
  bgDefault: '#1F1F1F', // level 1 (sm)
  bgSurface: '#1F1F1F',
  bgElevatedMd: '#2A2A2A', // level 2 (md)
  bgElevatedLg: '#333333', // level 3 (lg)
  bgElevatedXl: '#3A3A3A', // level 4 (xl)
  bgHover: '#3A3A3A',
  bgElevated: '#2A2A2A',

  // Status / chip surface tints ──────────────────────────────────────────────
  bgSuccess: palettes.laurel[950], // #0A2C16
  bgError: palettes.cinnabar[950], // #31050B
  bgWarning: palettes.saffron[950], // #2F2007
  bgInfo: palettes.lapis[950], // #0C202A

  // Text on chip tints — each pair clears AAA against its own bg.
  chipTextSuccess: palettes.laurel[200], // #A3EFBF on bgSuccess = 11.31:1 AAA
  chipTextError: palettes.cinnabar[200], // #FB97A5 on bgError = 8.70:1 AAA
  chipTextWarning: palettes.saffron[200], // #F7D59B on bgWarning = 11.23:1 AAA
  chipTextInfo: palettes.lapis[200], // #A6D5EC on bgInfo = 10.63:1 AAA

  // Text ──────────────────────────────────────────────────────────────────────
  textPrimary: '#F3F3F3', // 17.3:1 on bgBody — AAA
  textSecondary: palettes.pumice[200], // #C4C7CD — 11.3 / 8.5:1 AAA on body / elMd
  // textDisabled exempt from 1.4.3 but still readable at most elevations.
  textDisabled: palettes.pumice[400], // #9298A3 — 6.6 / 4.95:1
  textPlaceholder: palettes.pumice[300], // #ABB0B8 — 8.8 / 6.6:1 AAA
  textInverse: '#212121', // dark text for placement on bright fills
  textLink: palettes.lapis[200], // #A6D5EC — 12.2 / 9.1:1 AAA (was lapis[400])
  textError: palettes.cinnabar[200], // #FB97A5 — 9.2 / 6.9:1 AAA-ish (was cinnabar[400])
  textSuccess: palettes.laurel[200], // #A3EFBF — 14.3 / 10.7:1 AAA (was laurel[400])
  textWarning: palettes.saffron[200], // #F7D59B — 13.6 / 10.2:1 AAA
  textInfo: palettes.lapis[200], // #A6D5EC — 12.2 / 9.1:1 AAA
  textBrand: palettes.iris[100], // #D7C8F8 — 12.3 / 9.2:1 AAA (was iris[400] 2.6:1 fail)

  // Borders ───────────────────────────────────────────────────────────────────
  // Borders are visual chrome — they don't need to clear 3:1 against every
  // surface and lighter values let card chrome read as "calm" rather than
  // "framed". borderStrong is kept as a separate token (alias of
  // borderDefault) so callers can opt into a stronger border later without
  // a token rename.
  borderDefault: '#404040',
  borderStrong: '#404040', // alias — same value as borderDefault
  borderSubtle: '#2A2A2A', // decorative-only
  borderFocus: palettes.iris[400], // #7841F4 — original focus ring color

  // Solid-fill role colors ────────────────────────────────────────────────────
  // Original brand-identity values. The dark-mode shift to one step lighter
  // (e.g. tyrian[500] → tyrian[400]) is intentional: it gives buttons more
  // visual weight against the dark page background.
  primary: palettes.tyrian[400], // #7667CE
  secondary: palettes.madder[400], // #B958DD
  accent: palettes.iris[400], // #7841F4
  info: palettes.lapis[400], // #55B2E0
  success: palettes.laurel[400], // #4FE687
  error: palettes.cinnabar[400], // #FE3752
  warning: palettes.saffron[400], // #F6B23F
};
